import clsx from 'clsx';
import { motion } from 'framer-motion';
import { FC } from 'react';
import { ListItem, ListItemProps } from '../../UI/components/List/ListItem';
import { LinkedAccount, MappedAccountStatus } from '../../generated/graphql';
import LinkedAccountTooltip from '../../UI/pages/InsuranceRateElementDetailView/components/LinkedAccountTooltip';
import WarningSolidIcon from '../assets/warningSolidIcon';
import { useFeatureFlagsContext } from '../../FeatureFlags/context/featureFlags.context';

export interface MonetaryListItemProps
  extends ListItemProps,
    Pick<LinkedAccount, 'accountLinkProvider' | 'linkStatus' | 'linkedInstitutionName'>,
    Pick<ListItemProps, 'withChevron'> {
  title: string;
  amount: string;
  danger?: boolean;
  subtitle?: string;
  withChevron?: boolean;
  amountSubtitle?: string;
  contentPrefix?: JSX.Element;
  contentSuffix?: JSX.Element;
  titleContentSuffix?: JSX.Element | boolean;
  isInsurance?: boolean;
}

export const SharedMonetaryListItem: FC<MonetaryListItemProps> = ({
  title,
  index,
  amount,
  danger,
  onClick,
  subtitle,
  className,
  linkStatus,
  withChevron,
  contentPrefix,
  contentSuffix,
  amountSubtitle,
  titleContentSuffix,
  accountLinkProvider,
  linkedInstitutionName,
}) => {
  const { allowBAA } = useFeatureFlagsContext();

  return (
    <ListItem index={index} className={className} withChevron={withChevron}>
      <div className="w-full flex">
        {contentPrefix}
        <div onClick={onClick} className="MonetaryListItem-main-content flex-1 grid grid-cols-[1fr_auto]">
          <div className="MonetaryListItem-title-content relative mr-6 flex flex-col justify-between">
            {linkStatus &&
              ((jsx: JSX.Element) =>
                allowBAA ? (
                  <LinkedAccountTooltip
                    offset={[-7, -1]}
                    placement="top-end"
                    linkStatus={linkStatus}
                    accountLinkProvider={accountLinkProvider}
                    linkedInstitutionName={linkedInstitutionName}
                  >
                    {jsx}
                  </LinkedAccountTooltip>
                ) : (
                  jsx
                ))(
                <motion.div
                  whileHover={{ scale: 1.5 }}
                  className={clsx(
                    'absolute left-[-7px] top-2 mr-1 h-1 w-1 rounded-md',
                    linkStatus === MappedAccountStatus.Active ? 'bg-palette-600' : 'bg-darkMode-danger'
                  )}
                />
              )}
            <div className="flex items-start">
              <p className="MonetaryListItem-title line-clamp-3 text-base font-medium leading-5 tracking-[-0.34px] text-white">
                {title}
              </p>
              {titleContentSuffix}
            </div>
            {subtitle && (
              <p className="MonetaryListItem-subtitle mt-[-2px] text-[13px] text-opacity-70 text-darkMode-gray-light">
                {subtitle}
              </p>
            )}
          </div>
          <div className="MonetaryListItem-amount-content flex flex-col items-end text-darkMode-gray-medium">
            <h4
              className={clsx(
                'MonetaryListItem-amount text-lg leading-[22px] text-right font-bold tracking-[-0.39px]',
                danger ? 'text-[#ffe26c]' : 'text-white'
              )}
            >
              ${amount}
            </h4>
            {amountSubtitle && (
              <span className="MonetaryListItem-amount-subtitle mr-[2px] mt-[-2px] flex items-end whitespace-nowrap text-right text-[13px]">
                {danger && (
                  <span className="relative bottom-[4px]">
                    <WarningSolidIcon className="text-darkMode-warning flex-shrink-0" />
                  </span>
                )}
                <span
                  className={clsx('text-right', danger ? 'text-[#ffe26c]' : 'text-darkMode-gray-light text-opacity-70')}
                >
                  {amountSubtitle}
                </span>
              </span>
            )}
          </div>
        </div>
        {contentSuffix}
      </div>
    </ListItem>
  );
};
